import React, { useState, useEffect } from 'react';
import { FaWhatsapp, FaPaperPlane, FaUserPlus, FaFileDownload, FaUsers, FaCog, FaQuestionCircle, FaKey, FaTimes, FaInbox, FaReply, FaCheckCircle, FaBell, FaSearch, FaExclamationTriangle, FaVideo, FaMusic, FaFile, FaImage, FaDownload } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import { useSubscription } from '../../hooks/useSubscription';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';
import WhatsAppPolls from './WhatsAppPolls';

// Constants for localStorage keys
const WHATSAPP_CONNECTION_KEY = 'whatsapp_connection_state';
const WHATSAPP_CONFIG_KEY = 'whatsapp_config';

// Add a tabs enum
const TABS = {
  MAIN: 'main',
  INBOX: 'inbox',
  POLLS: 'polls',
  SETTINGS: 'settings'
};

/**
 * School WhatsApp integration component
 * Allows schools to send WhatsApp messages and manage WhatsApp communication
 */
const SchoolWhatsappIntegration = () => {
  const { showToast } = useToast();
  const { hasTier } = useSubscription();
  
  // State for WhatsApp integration
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showNewTemplateModal, setShowNewTemplateModal] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', content: '' });
  const [stats, setStats] = useState({ sent: 0, delivered: 0, read: 0, failed: 0, received: 0 });
  const [statsLoading, setStatsLoading] = useState(false);
  
  // New state for messages inbox
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesPage, setMessagesPage] = useState(1);
  const [messagesTotalPages, setMessagesTotalPages] = useState(1);
  const [messagesSearch, setMessagesSearch] = useState('');
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);
  
  // New state for active tab
  const [activeTab, setActiveTab] = useState(TABS.MAIN);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [showRespondModal, setShowRespondModal] = useState(false);
  
  // State for add contact feature
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    number: '',
    role: 'parent' // default role
  });
  
  // State for file import
  const [showImportModal, setShowImportModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedGroupForImport, setSelectedGroupForImport] = useState('');
  
  // State for group management
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: ''
  });
  const [selectedContactsForGroup, setSelectedContactsForGroup] = useState([]);
  const [showAddToGroupModal, setShowAddToGroupModal] = useState(false);
  const [selectedGroupForAdd, setSelectedGroupForAdd] = useState('');
  
  // API configuration
  const [whatsappConfig, setWhatsappConfig] = useState({
    apiToken: '',
    phoneNumberId: '',
    verifyToken: '',
    appId: ''
  });
  
  // Add state for webhook troubleshooting
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [showWebhookInfo, setShowWebhookInfo] = useState(false);
  
  // Add state for media message functionality
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaType, setMediaType] = useState('');
  const [mediaCaption, setMediaCaption] = useState('');
  const [showMediaUpload, setShowMediaUpload] = useState(false);
  const [responseMediaFile, setResponseMediaFile] = useState(null);
  const [responseMediaType, setResponseMediaType] = useState('');
  const [showMediaUploadResponse, setShowMediaUploadResponse] = useState(false);
  
  // Check if user has WhatsApp integration access
  const hasAccess = hasTier(['elevate']);
  
  // Load saved WhatsApp connection state from localStorage on mount
  useEffect(() => {
    if (hasAccess) {
      // Try to get saved connection state and config from localStorage
      const savedConnectionState = localStorage.getItem(WHATSAPP_CONNECTION_KEY);
      const savedConfig = localStorage.getItem(WHATSAPP_CONFIG_KEY);
      
      if (savedConnectionState === 'true') {
        setConnected(true);
      }
      
      if (savedConfig) {
        try {
          const parsedConfig = JSON.parse(savedConfig);
          setWhatsappConfig(parsedConfig);
        } catch (error) {
          console.error('Error parsing saved WhatsApp config:', error);
        }
      }
      
      // Still fetch from server to ensure data is up-to-date
      fetchWhatsAppStatus();
      fetchTemplates();
    }
  }, [hasAccess]);
  
  // Fetch WhatsApp connection status
  const fetchWhatsAppStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/schools/whatsapp/status');
      
      // Log the response to debug connection status
      console.log('WhatsApp status response:', response.data);
      
      // Make sure we check if connected property exists before using it
      const isConnected = response.data.data && response.data.data.connected === true;
      setConnected(isConnected);
      
      // Save connection state to localStorage
      localStorage.setItem(WHATSAPP_CONNECTION_KEY, isConnected.toString());
      
      // Handle configuration
      let config = null;
      
      // If there's config in the response, use it
      if (isConnected && response.data.data.config) {
        config = response.data.data.config;
        
        // Save config to localStorage regardless of masking
        localStorage.setItem(WHATSAPP_CONFIG_KEY, JSON.stringify(config));
        
        // Update our state
        setWhatsappConfig(config);
        
        console.log('WhatsApp configuration loaded from server and stored locally');
      } 
      // If not in response but we're connected, try to load from localStorage
      else if (isConnected && localStorage.getItem(WHATSAPP_CONFIG_KEY)) {
        try {
          const savedConfig = JSON.parse(localStorage.getItem(WHATSAPP_CONFIG_KEY));
          if (savedConfig) {
            config = savedConfig;
            setWhatsappConfig(savedConfig);
          }
        } catch (error) {
          console.error('Error parsing saved WhatsApp config:', error);
        }
      }
      
      if (isConnected) {
        console.log('WhatsApp is connected with configuration:', {
          hasApiToken: !!config?.apiToken,
          hasPhoneNumberId: !!config?.phoneNumberId,
          hasVerifyToken: !!config?.verifyToken
        });
        
        fetchStats();
        fetchContacts();
        fetchGroups();
      }
    } catch (error) {
      console.error('Error fetching WhatsApp status:', error);
      showToast('Failed to check WhatsApp connection status', 'error');
      
      // Even if API call fails, try to read from localStorage
      const savedConnectionState = localStorage.getItem(WHATSAPP_CONNECTION_KEY);
      const savedConfig = localStorage.getItem(WHATSAPP_CONFIG_KEY);
      
      if (savedConnectionState === 'true') {
        setConnected(true);
        console.log('Using saved connection state from localStorage');
        
        if (savedConfig) {
          try {
            const parsedConfig = JSON.parse(savedConfig);
            setWhatsappConfig(parsedConfig);
            console.log('Using saved WhatsApp config from localStorage');
          } catch (parseError) {
            console.error('Error parsing saved WhatsApp config:', parseError);
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch contacts and groups
  const fetchContacts = async () => {
    if (!connected && !loading) return;
    
    try {
      const contactsResponse = await api.get('/schools/whatsapp/contacts');
      console.log('Contacts API response:', contactsResponse.data);
      
      // Ensure each contact has required fields and properly formatted numbers
      const processedContacts = (contactsResponse.data.data || []).map(contact => {
        // Extract the phone number from whatever field it's in
        let phoneNumber = contact.number || contact.phoneNumber || '';
        
        // Skip processing if no phone number
        if (!phoneNumber) {
          console.warn('Contact missing phone number:', contact);
          return contact; // Keep the contact but it won't be usable for messaging
        }
        
        // Format phone number for WhatsApp API (must start with + and have no spaces)
        if (!phoneNumber.startsWith('+')) {
          phoneNumber = `+${phoneNumber.replace(/^0/, '')}`;
        }
        
        phoneNumber = phoneNumber.replace(/\s+/g, '');
        
        // Return a normalized contact object with correct field names
        return { 
          ...contact,
          number: phoneNumber,
          // Ensure ID exists (use existing ID or MongoDB _id)
          id: contact.id || contact._id || `contact-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        };
      });
      
      console.log('Processed contacts:', processedContacts);
      setContacts(processedContacts);
      
      const groupsResponse = await api.get('/schools/whatsapp/groups');
      console.log('Groups API response:', groupsResponse.data);
      
      // Ensure each group has required fields
      const processedGroups = (groupsResponse.data.data || []).map(group => {
        // Extract the identifier (either number or chatId)
        let identifier = group.number || group.chatId || group.id || '';
        
        // Format group identifier if it's a phone number
        if (identifier && typeof identifier === 'string' && /^\+?\d+$/.test(identifier)) {
          if (!identifier.startsWith('+')) {
            identifier = `+${identifier.replace(/^0/, '')}`;
          }
          identifier = identifier.replace(/\s+/g, '');
        }
        
        return { 
          ...group,
          number: identifier,
          // Ensure group has a proper ID (use MongoDB _id if available)
          id: group.id || group._id || `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          // Ensure we know it's a group for type determination
          memberCount: group.memberCount || (group.members ? group.members.length : 0) || 1
        };
      });
      
      console.log('Processed groups:', processedGroups);
      setGroups(processedGroups);
    } catch (error) {
      console.error('Error fetching WhatsApp contacts/groups:', error);
      showToast('Failed to load WhatsApp contacts and groups', 'error');
    }
  };
  
  // Fetch groups separately (for refreshing after operations)
  const fetchGroups = async () => {
    if (!connected && !loading) return;
    
    try {
      const groupsResponse = await api.get('/schools/whatsapp/groups');
      console.log('Groups API response:', groupsResponse.data);
      
      // Ensure each group has required fields
      const processedGroups = (groupsResponse.data.data || []).map(group => {
        // Extract the identifier (either number or chatId)
        let identifier = group.number || group.chatId || group.id || '';
        
        // Format group identifier if it's a phone number
        if (identifier && typeof identifier === 'string' && /^\+?\d+$/.test(identifier)) {
          if (!identifier.startsWith('+')) {
            identifier = `+${identifier.replace(/^0/, '')}`;
          }
          identifier = identifier.replace(/\s+/g, '');
        }
        
        return { 
          ...group,
          number: identifier,
          // Ensure group has a proper ID (use MongoDB _id if available)
          id: group.id || group._id || `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          // Ensure we know it's a group for type determination
          memberCount: group.memberCount || (group.members ? group.members.length : 0) || 1
        };
      });
      
      console.log('Processed groups:', processedGroups);
      setGroups(processedGroups);
    } catch (error) {
      console.error('Error fetching WhatsApp groups:', error);
      showToast('Failed to load WhatsApp groups', 'error');
    }
  };
  
  // Fetch message templates
  const fetchTemplates = async () => {
    try {
      const response = await api.get('/schools/whatsapp/templates');
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Error fetching message templates:', error);
      showToast('Failed to load message templates', 'error');
    }
  };
  
  // Fetch messaging statistics
  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await api.get('/schools/whatsapp/stats');
      
      if (response.data && response.data.success) {
        const statsData = response.data.data || { sent: 0, delivered: 0, read: 0, failed: 0, received: 0 };
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching WhatsApp stats:', error);
      showToast('Failed to load messaging statistics', 'error');
    } finally {
      setStatsLoading(false);
    }
  };
  
  // Fetch stats periodically when connected
  useEffect(() => {
    if (connected) {
      fetchStats(); // Fetch immediately when connected
      
      // Refresh stats every 5 minutes
      const statsInterval = setInterval(() => {
        fetchStats();
      }, 5 * 60 * 1000);
      
      // Clean up interval on unmount
      return () => clearInterval(statsInterval);
    }
  }, [connected]);
  
  // Add a useEffect to fetch messages when inbox is shown
  useEffect(() => {
    if (connected && activeTab === TABS.INBOX) {
      // First, ensure WhatsApp configuration is loaded
      const loadConfig = async () => {
        await ensureWhatsappConfig();
        fetchMessages();
      };
      
      loadConfig();
      
      // Refresh messages every 30 seconds when inbox is open
      const messagesInterval = setInterval(() => {
        fetchMessages();
      }, 30 * 1000);
      
      return () => clearInterval(messagesInterval);
    }
  }, [connected, activeTab, messagesPage, messagesSearch, showOnlyUnread]);
  
  // Function to ensure WhatsApp configuration is loaded
  const ensureWhatsappConfig = async () => {
    // Check if config is already loaded
    if (whatsappConfig.apiToken && whatsappConfig.phoneNumberId) {
      return;
    }
    
    // Try to load from localStorage
    if (localStorage.getItem(WHATSAPP_CONFIG_KEY)) {
      try {
        const savedConfig = JSON.parse(localStorage.getItem(WHATSAPP_CONFIG_KEY));
        
        if (savedConfig.apiToken || savedConfig.phoneNumberId) {
          setWhatsappConfig(prevConfig => ({
            ...prevConfig,
            apiToken: savedConfig.apiToken || prevConfig.apiToken,
            phoneNumberId: savedConfig.phoneNumberId || prevConfig.phoneNumberId,
            verifyToken: savedConfig.verifyToken || prevConfig.verifyToken,
            appId: savedConfig.appId || prevConfig.appId
          }));
          
          console.log('Loaded WhatsApp configuration from localStorage');
          return;
        }
      } catch (configError) {
        console.error('Error parsing saved WhatsApp config:', configError);
      }
    }
    
    // If we reach here, we couldn't load from localStorage, try to fetch from server
    try {
      console.log('Configuration not found in localStorage, fetching from server...');
      const response = await api.get('/schools/whatsapp/status');
      
      if (response.data?.data?.connected && response.data?.data?.config) {
        // Update the state with the configuration from the server
        const serverConfig = response.data.data.config;
        
        setWhatsappConfig(prevConfig => ({
          ...prevConfig,
          apiToken: serverConfig.apiToken || prevConfig.apiToken,
          phoneNumberId: serverConfig.phoneNumberId || prevConfig.phoneNumberId,
          verifyToken: serverConfig.verifyToken || prevConfig.verifyToken,
          appId: serverConfig.appId || prevConfig.appId
        }));
        
        // Save the config to localStorage
        localStorage.setItem(WHATSAPP_CONFIG_KEY, JSON.stringify(serverConfig));
        
        console.log('Loaded WhatsApp configuration from server');
      }
    } catch (error) {
      console.error('Error fetching WhatsApp configuration from server:', error);
    }
  };
  
  // Add function to fetch messages
  const fetchMessages = async () => {
    if (!connected) return;
    
    try {
      setMessagesLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams({
        page: messagesPage,
        limit: 10
      });
      
      if (messagesSearch) {
        params.append('search', messagesSearch);
      }
      
      if (showOnlyUnread) {
        params.append('unread', 'true');
      }
      
      const response = await api.get(`/schools/whatsapp/messages?${params.toString()}`);
      
      if (response.data && response.data.success) {
        setMessages(response.data.data);
        
        // Set pagination info
        if (response.data.pagination) {
          setMessagesTotalPages(response.data.pagination.pages);
        }
      }
    } catch (error) {
      console.error('Error fetching WhatsApp messages:', error);
      showToast('Failed to load WhatsApp messages', 'error');
    } finally {
      setMessagesLoading(false);
    }
  };
  
  // Function to mark a message as read
  const markMessageAsRead = async (messageId) => {
    try {
      await api.put(`/schools/whatsapp/messages/${messageId}/read`);
      
      // Update the read status in the local state
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg._id === messageId ? { ...msg, read: true } : msg
        )
      );
    } catch (error) {
      console.error('Error marking message as read:', error);
      showToast('Failed to mark message as read', 'error');
    }
  };
  
  // Function to handle message selection for response
  const handleSelectMessage = async (message) => {
    // Ensure WhatsApp configuration is loaded before opening response modal
    await ensureWhatsappConfig();
    
    setSelectedMessage(message);
    setShowRespondModal(true);
    
    // If message wasn't read yet, mark it as read
    if (!message.read) {
      markMessageAsRead(message._id);
    }
  };
  
  // Function to handle sending a response to a message
  const handleSendResponse = async () => {
    if (!selectedMessage || !responseMessage.trim()) {
      showToast('Please enter a response message', 'error');
      return;
    }
    
    try {
      setLoading(true);
      
      // Try to get WhatsApp configuration from state or localStorage
      let apiToken = whatsappConfig.apiToken;
      let phoneNumberId = whatsappConfig.phoneNumberId;
      
      // If configuration is missing from state, try to get it from localStorage
      if ((!apiToken || !phoneNumberId) && localStorage.getItem(WHATSAPP_CONFIG_KEY)) {
        try {
          const savedConfig = JSON.parse(localStorage.getItem(WHATSAPP_CONFIG_KEY));
          apiToken = savedConfig.apiToken || apiToken;
          phoneNumberId = savedConfig.phoneNumberId || phoneNumberId;
          
          // Update state with loaded configuration
          if (savedConfig.apiToken || savedConfig.phoneNumberId) {
            setWhatsappConfig(prevConfig => ({
              ...prevConfig,
              apiToken: savedConfig.apiToken || prevConfig.apiToken,
              phoneNumberId: savedConfig.phoneNumberId || prevConfig.phoneNumberId
            }));
          }
        } catch (configError) {
          console.error('Error parsing saved WhatsApp config:', configError);
        }
      }
      
      // If still missing configuration, show error
      if (!apiToken || !phoneNumberId) {
        showToast('WhatsApp configuration is incomplete. Please make sure you are connected to WhatsApp.', 'error');
        setLoading(false);
        return;
      }
      
      // Include WhatsApp configuration in the request payload
      const payload = {
        response: responseMessage.trim(),
        apiToken,
        phoneNumberId
      };
      
      console.log('Sending response with WhatsApp config:', { 
        hasApiToken: !!apiToken, 
        hasPhoneNumberId: !!phoneNumberId 
      });
      
      const response = await api.post(`/schools/whatsapp/messages/${selectedMessage._id}/respond`, payload);
      
      if (response.data && response.data.success) {
        showToast('Response sent successfully', 'success');
        
        // Update the message in the list
        setMessages(prevMessages => 
          prevMessages.map(msg => 
            msg._id === selectedMessage._id 
              ? { ...msg, responded: true, response: response.data.data.response }
              : msg
          )
        );
        
        // Close modal and reset state
        setShowRespondModal(false);
        setResponseMessage('');
        setSelectedMessage(null);
        
        // Refresh stats to show updated sent count
        fetchStats();
      }
    } catch (error) {
      console.error('Error sending response:', error);
      
      // Provide more specific error message
      let errorMsg = 'Failed to send response';
      if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      showToast(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Function to handle search in messages
  const handleMessagesSearch = (e) => {
    setMessagesSearch(e.target.value);
    setMessagesPage(1); // Reset to first page when search changes
  };
  
  // Add formatting function for phone numbers
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Basic formatting for display
    let formatted = phoneNumber;
    if (formatted.startsWith('+')) {
      // Add a space after country code for readability
      const countryCodeEnd = Math.min(formatted.length, 4);
      formatted = formatted.substring(0, countryCodeEnd) + ' ' + formatted.substring(countryCodeEnd);
    }
    
    return formatted;
  };
  
  // Function to format date/time
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  
  // Handle connecting WhatsApp with API credentials
  const handleConnect = async () => {
    // Validate inputs
    if (!whatsappConfig.apiToken.trim()) {
      showToast('WhatsApp API Token is required', 'error');
      return;
    }
    
    if (!whatsappConfig.phoneNumberId.trim()) {
      showToast('WhatsApp Phone Number ID is required', 'error');
      return;
    }
    
    if (!whatsappConfig.verifyToken.trim()) {
      showToast('WhatsApp Verify Token is required', 'error');
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.post('/schools/whatsapp/connect', {
        apiToken: whatsappConfig.apiToken.trim(),
        phoneNumberId: whatsappConfig.phoneNumberId.trim(),
        verifyToken: whatsappConfig.verifyToken.trim(),
        appId: whatsappConfig.appId.trim()
      });
      
      // Log connection response for debugging
      console.log('Connect WhatsApp response:', response.data);
      
      if (response.data && response.data.success) {
        setConnected(true);
        showToast('WhatsApp connected successfully', 'success');
        
        // Save connection state and config to localStorage
        localStorage.setItem(WHATSAPP_CONNECTION_KEY, 'true');
        localStorage.setItem(WHATSAPP_CONFIG_KEY, JSON.stringify(whatsappConfig));
        
        // Fetch contacts and stats directly without refetching status
        await fetchContacts();
        await fetchStats();
      } else {
        showToast(response.data?.message || 'Failed to connect to WhatsApp', 'error');
      }
    } catch (error) {
      console.error('Error connecting WhatsApp:', error);
      // Provide more specific error messages
      let errorMsg = 'Failed to connect to WhatsApp Business API';
      
      if (error.response?.data?.error?.includes('API token')) {
        errorMsg = 'Invalid API token. Please check your WhatsApp Business API credentials.';
      } else if (error.response?.data?.error?.includes('Phone Number ID')) {
        errorMsg = 'Invalid Phone Number ID. Please check your WhatsApp Business API credentials.';
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      showToast(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle disconnecting WhatsApp
  const handleDisconnect = async () => {
    try {
      setLoading(true);
      await api.post('/schools/whatsapp/disconnect');
      setConnected(false);
      
      // Remove connection state and config from localStorage
      localStorage.removeItem(WHATSAPP_CONNECTION_KEY);
      localStorage.removeItem(WHATSAPP_CONFIG_KEY);
      
      // Also clear from state so the form is reset
      setWhatsappConfig({
        apiToken: '',
        phoneNumberId: '',
        verifyToken: '',
        appId: ''
      });
      
      showToast('WhatsApp disconnected successfully', 'success');
    } catch (error) {
      console.error('Error disconnecting WhatsApp:', error);
      showToast('Failed to disconnect WhatsApp', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle config input changes
  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    setWhatsappConfig({
      ...whatsappConfig,
      [name]: value
    });
  };
  
  // Handle selecting a recipient
  const handleSelectRecipient = (recipient) => {
    if (selectedRecipients.some(r => r.id === recipient.id || r.id === recipient._id || r._id === recipient._id)) {
      setSelectedRecipients(selectedRecipients.filter(r => 
        (r.id !== recipient.id) && (r.id !== recipient._id) && (r._id !== recipient._id)
      ));
    } else {
      // Ensure the recipient has an id property (use _id if no id exists)
      const recipientWithId = {
        ...recipient,
        id: recipient.id || recipient._id  // Use existing id or _id
      };
      setSelectedRecipients([...selectedRecipients, recipientWithId]);
    }
  };
  
  // Handle message input change
  const handleMessageChange = (e) => {
    setMessage(e.target.value);
  };
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle template selection
  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setMessage(template.content);
  };
  
  // Handle new template form input
  const handleTemplateChange = (e) => {
    const { name, value } = e.target;
    setNewTemplate(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle creating a new template
  const handleCreateTemplate = async () => {
    if (!newTemplate.name.trim() || !newTemplate.content.trim()) {
      showToast('Template name and content are required', 'error');
      return;
    }
    
    try {
      const response = await api.post('/schools/whatsapp/templates', newTemplate);
      setTemplates([...templates, response.data.data]);
      setShowNewTemplateModal(false);
      setNewTemplate({ name: '', content: '' });
      showToast('Message template created successfully', 'success');
    } catch (error) {
      console.error('Error creating template:', error);
      showToast('Failed to create message template', 'error');
    }
  };
  
  // Filter contacts based on search term
  const filteredContacts = contacts.filter(contact => 
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.number.includes(searchTerm)
  );
  
  // Filter groups based on search term
  const filteredGroups = groups.filter(group => 
    group.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Handle sending message with improved error handling
  const handleSendMessage = async () => {
    // Input validation
    if (!message.trim()) {
      showToast('Please enter a message', 'error');
      return;
    }
    
    if (selectedRecipients.length === 0) {
      showToast('Please select at least one recipient', 'error');
      return;
    }
    
    // Define messageData outside the try block so it's accessible in the catch block
    let messageData = null;
    
    try {
      setLoading(true);
      
      // Enhanced validation and logging for recipients
      console.log('Selected recipients before mapping:', selectedRecipients);
      
      // Validate recipients have required fields and format numbers properly
      const validRecipients = selectedRecipients.filter(r => {
        // First check if recipient has basic required fields
        if (!r || (!r.id && !r._id)) {
          console.warn('Invalid recipient (missing ID):', r);
          return false;
        }
        
        // Check if recipient has a number and it's in valid format
        let phoneNumber = r.number || r.phoneNumber || null;
        
        // Skip recipients without numbers
        if (!phoneNumber) {
          console.warn('Invalid recipient (missing number):', r);
          return false;
        }
        
        // Ensure number has proper formatting (includes country code with + sign)
        if (!phoneNumber.startsWith('+')) {
          console.warn('Number missing + prefix:', phoneNumber);
          // Don't reject the recipient, we'll fix this below
        }
        
        return true;
      }).map(r => {
        // Create a normalized recipient object with proper number formatting
        let phoneNumber = r.number || r.phoneNumber;
        
        // Remove any non-digit characters except the leading +
        phoneNumber = phoneNumber.replace(/[^\d+]/g, '');
        
        // Ensure the number starts with a + (required for WhatsApp API)
        if (!phoneNumber.startsWith('+')) {
          phoneNumber = `+${phoneNumber.replace(/^0/, '')}`;
        }
        
        // Validate the E.164 format (+ followed by 10-15 digits)
        const phoneRegex = /^\+[0-9]{10,15}$/;
        if (!phoneRegex.test(phoneNumber)) {
          console.warn(`Phone number ${phoneNumber} does not match E.164 format`);
          // Still include it but log the warning
        }
        
        return {
          id: r.id || r._id,  // Use either id or _id
          number: phoneNumber,
          type: r.memberCount ? 'group' : 'contact',
          name: r.name // Include name for better logging
        };
      });
      
      if (validRecipients.length === 0) {
        showToast('No valid recipients with phone numbers selected', 'error');
        setLoading(false);
        return;
      }
      
      if (validRecipients.length < selectedRecipients.length) {
        const invalidCount = selectedRecipients.length - validRecipients.length;
        showToast(`Warning: ${invalidCount} recipient(s) were removed due to missing phone numbers`, 'warning');
      }
      
      // Log each recipient for debugging
      console.log('Valid recipients after processing:', validRecipients);
      validRecipients.forEach((r, i) => {
        console.log(`Recipient ${i+1}: ID=${r.id}, Number=${r.number}, Type=${r.type}, Name=${r.name}`);
      });
      
      // Prepare the request data
      messageData = {
        recipients: validRecipients.map(r => ({
          phoneNumber: r.number, // Ensure we're using the standard field name expected by API
          id: r.id,
          name: r.name,
          type: r.type || 'contact' // Default to contact type if not specified
        })),
        message: message.trim(),
        templateId: selectedTemplate ? selectedTemplate.id : null,
        // Add WhatsApp configuration if available from local state
        apiToken: whatsappConfig.apiToken,
        phoneNumberId: whatsappConfig.phoneNumberId
      };
      
      // Validate WhatsApp configuration is available
      if (!messageData.apiToken || !messageData.phoneNumberId) {
        // Try to fetch the latest configuration
        try {
          const statusResponse = await api.get('/schools/whatsapp/status');
          if (statusResponse.data?.data?.config) {
            messageData.apiToken = statusResponse.data.data.config.apiToken;
            messageData.phoneNumberId = statusResponse.data.data.config.phoneNumberId;
            
            // Update local state
            setWhatsappConfig(prev => ({
              ...prev,
              apiToken: messageData.apiToken || prev.apiToken,
              phoneNumberId: messageData.phoneNumberId || prev.phoneNumberId
            }));
          }
        } catch (configError) {
          console.error('Failed to fetch WhatsApp configuration:', configError);
        }
        
        // Final check if we have the required configuration
        if (!messageData.apiToken || !messageData.phoneNumberId) {
          showToast('WhatsApp configuration is incomplete. Please check your API token and phone number ID.', 'error');
          setLoading(false);
          return;
        }
      }
      
      // Additional check to ensure the message has content
      if (!messageData.message.trim()) {
        showToast('Message content cannot be empty', 'error');
        setLoading(false);
        return;
      }
      
      // Additional check to ensure there's at least one valid recipient
      if (!messageData.recipients || messageData.recipients.length === 0) {
        showToast('No valid recipients found. Please check phone numbers.', 'error');
        setLoading(false);
        return;
      }
      
      console.log('Sending WhatsApp message with data:', messageData);
      
      const response = await api.post('/schools/whatsapp/send', messageData);
      
      console.log('WhatsApp send message response:', response.data);
      
      if (response.data && response.data.success) {
        const messagesSent = response.data.data?.messagesSent || 0;
        const messagesFailed = response.data.data?.messagesFailed || 0;
        
        if (messagesSent > 0 && messagesFailed === 0) {
          // All messages sent successfully
          showToast('Message sent successfully', 'success');
          setMessage('');
          setSelectedRecipients([]);
          setSelectedTemplate(null);
        } else if (messagesSent > 0 && messagesFailed > 0) {
          // Some messages sent, some failed
          showToast(`Partially successful: ${messagesSent} messages sent, ${messagesFailed} failed.`, 'warning');
          setMessage('');
          setSelectedRecipients([]);
          setSelectedTemplate(null);
        } else {
          // All messages failed
          showToast('Failed to send messages. Please check recipient numbers or your WhatsApp configuration.', 'error');
        }
        
        // Refresh stats to show updated counts
        setTimeout(() => fetchStats(), 1000);
      } else {
        showToast(response.data?.message || 'Failed to send message', 'error');
      }
    } catch (error) {
      console.error('Error sending WhatsApp message:', error);
      // More detailed error handling
      let errorMsg = 'Failed to send message';
      
      // Check for specific WhatsApp API configuration errors
      if (error.response?.data?.error?.includes('WhatsApp API configuration')) {
        errorMsg = 'WhatsApp API configuration is incomplete. Please check your API token and phone number ID.';
      } else if (error.response?.data?.error === 'No valid recipients found') {
        errorMsg = 'No valid recipients found. Please ensure all phone numbers are in international format (+XXXXXXXXXXX).';
        // Only log if messageData is defined
        if (messageData && messageData.recipients) {
          console.warn('Recipients that were attempted:', messageData.recipients);
        }
      } else if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      showToast(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle importing contacts from file
  const handleImportContacts = async () => {
    if (!importFile) {
      showToast('Please select a file to import', 'error');
      return;
    }
    
    const formData = new FormData();
    formData.append('contactsFile', importFile);
    
    // Add group ID if selected
    if (selectedGroupForImport) {
      formData.append('groupId', selectedGroupForImport);
    }
    
    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      // Add more detailed info for the user
      showToast('Starting contact import. This may take a moment...', 'info');
      
      const response = await api.post('/schools/whatsapp/contacts/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      console.log('Import contacts response:', response.data);
      
      if (response.data && response.data.success) {
        const imported = response.data.data?.imported || 0;
        const failed = response.data.data?.failed || 0;
        const total = response.data.data?.total || imported + failed;
        const groupName = response.data.data?.groupName;
        
        let successMessage = `Successfully imported ${imported} contacts`;
        if (groupName) {
          successMessage += ` to group "${groupName}"`;
        }
        
        if (failed > 0) {
          showToast(
            `Partially successful: Imported ${imported} of ${total} contacts${groupName ? ` to group "${groupName}"` : ''}. ${failed} failed due to invalid data.`, 
            'warning'
          );
        } else {
          showToast(successMessage, 'success');
        }
        
        // Reset states and close modal
        setImportFile(null);
        setSelectedGroupForImport('');
        setShowImportModal(false);
        
        // Refresh contacts and groups list
        fetchContacts();
        fetchGroups();
      } else {
        showToast(response.data?.message || 'Failed to import contacts', 'error');
      }
    } catch (error) {
      console.error('Error importing contacts:', error);
      
      // Provide more specific error messages based on error type
      let errorMsg = 'Failed to import contacts';
      
      if (error.response?.status === 413) {
        errorMsg = 'File too large. Please upload a smaller file (max 5MB).';
      } else if (error.response?.status === 422) {
        errorMsg = 'Invalid file format or data. Please check your file format and content.';
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      
      showToast(errorMsg, 'error');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };
  
  // Get recipient display text
  const getRecipientDisplayText = () => {
    if (selectedRecipients.length === 0) {
      return 'No recipients selected';
    } else if (selectedRecipients.length === 1) {
      return selectedRecipients[0].name;
    } else {
      return `${selectedRecipients.length} recipients selected`;
    }
  };
  
  // Handle new contact form input
  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setNewContact(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle creating a new group
  const handleCreateGroup = async () => {
    if (!newGroup.name.trim()) {
      showToast('Group name is required', 'error');
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await api.post('/schools/whatsapp/groups', {
        name: newGroup.name.trim(),
        description: newGroup.description.trim()
      });
      
      if (response.data && response.data.success) {
        showToast('Group created successfully', 'success');
        setNewGroup({ name: '', description: '' });
        setShowCreateGroupModal(false);
        fetchGroups();
      }
    } catch (error) {
      console.error('Error creating group:', error);
      showToast(error.response?.data?.message || 'Failed to create group', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle adding selected contacts to a group
  const handleAddContactsToGroup = async () => {
    if (!selectedGroupForAdd) {
      showToast('Please select a group', 'error');
      return;
    }
    
    if (selectedContactsForGroup.length === 0) {
      showToast('Please select contacts to add', 'error');
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await api.put(`/schools/whatsapp/groups/${selectedGroupForAdd}/members`, {
        contactIds: selectedContactsForGroup
      });
      
      if (response.data && response.data.success) {
        const groupName = groups.find(g => g._id === selectedGroupForAdd)?.name || 'Selected group';
        showToast(`Added ${selectedContactsForGroup.length} contacts to ${groupName}`, 'success');
        setSelectedContactsForGroup([]);
        setSelectedGroupForAdd('');
        setShowAddToGroupModal(false);
        fetchGroups();
      }
    } catch (error) {
      console.error('Error adding contacts to group:', error);
      showToast(error.response?.data?.message || 'Failed to add contacts to group', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Toggle contact selection for group management
  const toggleContactForGroup = (contactId) => {
    setSelectedContactsForGroup(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };
  
  // Handle creating a new contact
  const handleAddContact = async () => {
    // Validate contact information
    if (!newContact.name.trim()) {
      showToast('Contact name is required', 'error');
      return;
    }
    
    if (!newContact.number.trim()) {
      showToast('Phone number is required', 'error');
      return;
    }
    
    // Format and validate phone number
    let phoneNumber = newContact.number.trim();
    
    // Remove any spaces or dashes
    phoneNumber = phoneNumber.replace(/[\s-]/g, '');
    
    // Check if number has country code (starts with +)
    if (!phoneNumber.startsWith('+')) {
      // Add the plus prefix and remove leading 0 if present
      phoneNumber = `+${phoneNumber.replace(/^0/, '')}`;
    }
    
    // More comprehensive WhatsApp number validation
    // WhatsApp numbers should start with + followed by country code and number
    // Total length should be between 10 and 15 digits (including country code)
    const phoneRegex = /^\+[0-9]{9,15}$/;
    if (!phoneRegex.test(phoneNumber)) {
      showToast('Please enter a valid international phone number (e.g., +254712345678)', 'error');
      return;
    }
    
    try {
      setLoading(true);
      
      // Create a contact with formatted phone number
      const contactData = {
        ...newContact,
        number: phoneNumber
      };
      
      console.log('Adding contact with data:', contactData);
      
      const response = await api.post('/schools/whatsapp/contacts', contactData);
      
      if (response.data && response.data.success) {
        // Add the new contact to the state
        setContacts([...contacts, response.data.data]);
        setShowAddContactModal(false);
        setNewContact({ name: '', number: '', role: 'parent' });
        showToast('Contact added successfully', 'success');
      } else {
        showToast(response.data?.message || 'Failed to add contact', 'error');
      }
    } catch (error) {
      console.error('Error adding contact:', error);
      const errorMsg = error.response?.data?.message || 'Failed to add contact';
      showToast(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle file drag events
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      validateAndSetFile(file);
    }
  };
  
  // Validate file and set it for import
  const validateAndSetFile = (file) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = ['.csv', '.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'];
    const fileType = file.type;
    const fileExtension = file.name.substr(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!validTypes.includes(fileType) && !validTypes.includes(fileExtension)) {
      showToast('Please select a CSV or Excel file', 'error');
      return;
    }
    
    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      showToast('File size should be less than 5MB', 'error');
      return;
    }
    
    setImportFile(file);
    setShowImportModal(true);
  };
  
  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    validateAndSetFile(file);
  };
  
  // Handle sample template download
  const handleDownloadSample = () => {
    // CSV sample data
    const headers = "name,number,role\n";
    const sampleData = [
      "John Doe,+254712345678,parent",
      "Jane Smith,+254723456789,teacher",
      "Student Name,+254734567890,student"
    ].join("\n");
    
    const csvContent = headers + sampleData;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', 'whatsapp_contacts_sample.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  
  // Fetch webhook status for troubleshooting
  const fetchWebhookStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/schools/whatsapp/status');
      
      // Create webhook status data from the config data in the response
      const config = response.data.data.config || {};
      const webhookUrl = 'https://meira-server.onrender.com/api/schools/whatsapp/webhook';
      
      const transformedWebhookStatus = {
        webhookUrl: webhookUrl,
        verifyToken: config.verifyToken || 'Token is masked for security',
        configuredAt: response.data.data.connected ? Date.now() : null,
        apiTokenPreview: config.apiToken || 'Not configured',
        instructions: [
          "1. Use this exact URL in Meta dashboard: " + webhookUrl,
          "2. Use the exact same Verify Token you entered in the WhatsApp connection form",
          "3. Subscribe to at least 'messages' and 'message_status' webhook fields",
          "4. Make sure your server is publicly accessible"
        ],
        systemVerifyToken: 'Verify your token is correctly set'
      };
      
      setWebhookStatus(transformedWebhookStatus);
      setShowWebhookInfo(true);
    } catch (error) {
      console.error('Error fetching webhook status:', error);
      showToast('Unable to fetch webhook status', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Manual webhook setup function
  const handleManualWebhookSetup = async () => {
    try {
      // If we have an appId, open the specific webhook page, otherwise open the general Facebook Developer page
      if (whatsappConfig.appId && whatsappConfig.appId.trim()) {
        window.open(`https://developers.facebook.com/apps/${whatsappConfig.appId.trim()}/webhooks/`, '_blank');
      } else {
        window.open('https://developers.facebook.com/apps/', '_blank');
        showToast('Please select your WhatsApp app and navigate to Webhooks settings', 'info');
      }
    } catch (error) {
      console.error('Error opening webhook setup page:', error);
      showToast('Please visit developers.facebook.com to set up your webhook manually', 'info');
    }
  };
  
  // Handle media file selection
  const handleMediaFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size
    const maxSize = 16 * 1024 * 1024; // 16MB max for WhatsApp media
    if (file.size > maxSize) {
      showToast('File size should be less than 16MB for WhatsApp media', 'error');
      return;
    }
    
    // Determine media type from file
    const fileType = file.type;
    let detectedMediaType = '';
    
    if (fileType.startsWith('image/')) {
      detectedMediaType = 'image';
    } else if (fileType.startsWith('audio/')) {
      detectedMediaType = 'audio';
    } else if (fileType.startsWith('video/')) {
      detectedMediaType = 'video';
    } else {
      detectedMediaType = 'document';
    }
    
    setMediaFile(file);
    setMediaType(detectedMediaType);
  };
  
  // Handle response media file selection
  const handleResponseMediaFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size
    const maxSize = 16 * 1024 * 1024; // 16MB max for WhatsApp media
    if (file.size > maxSize) {
      showToast('File size should be less than 16MB for WhatsApp media', 'error');
      return;
    }
    
    // Determine media type from file
    const fileType = file.type;
    let detectedMediaType = '';
    
    if (fileType.startsWith('image/')) {
      detectedMediaType = 'image';
    } else if (fileType.startsWith('audio/')) {
      detectedMediaType = 'audio';
    } else if (fileType.startsWith('video/')) {
      detectedMediaType = 'video';
    } else {
      detectedMediaType = 'document';
    }
    
    setResponseMediaFile(file);
    setResponseMediaType(detectedMediaType);
    setShowMediaUploadResponse(true);
  };
  
  // Handle sending media message
  const handleSendMediaMessage = async () => {
    if (!mediaFile) {
      showToast('Please select a media file to send', 'error');
      return;
    }
    
    if (selectedRecipients.length === 0) {
      showToast('Please select at least one recipient', 'error');
      return;
    }
    
    try {
      setLoading(true);
      
      // Create form data for file upload
      const formData = new FormData();
      formData.append('mediaFile', mediaFile);
      formData.append('mediaType', mediaType);
      formData.append('caption', mediaCaption || '');
      
      // Convert selected recipients to JSON string
      formData.append('recipients', JSON.stringify(selectedRecipients));
      
      // Add WhatsApp API configuration
      // First ensure we have the configuration
      await ensureWhatsappConfig();
      
      // Include API credentials in the request
      if (whatsappConfig.apiToken) {
        formData.append('apiToken', whatsappConfig.apiToken);
      }
      
      if (whatsappConfig.phoneNumberId) {
        formData.append('phoneNumberId', whatsappConfig.phoneNumberId);
      }
      
      // Send request to API
      const response = await api.post('/schools/whatsapp/send-media', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      if (response.data && response.data.success) {
        const messagesSent = response.data.data?.messagesSent || 0;
        const messagesFailed = response.data.data?.messagesFailed || 0;
        
        if (messagesSent > 0 && messagesFailed === 0) {
          showToast('Media message sent successfully', 'success');
          setMediaFile(null);
          setMediaCaption('');
          setSelectedRecipients([]);
          setShowMediaUpload(false);
        } else if (messagesSent > 0 && messagesFailed > 0) {
          showToast(`Partially successful: ${messagesSent} messages sent, ${messagesFailed} failed.`, 'warning');
          setMediaFile(null);
          setMediaCaption('');
          setSelectedRecipients([]);
          setShowMediaUpload(false);
        } else {
          showToast('Failed to send media messages. Please check recipient numbers or your WhatsApp configuration.', 'error');
        }
        
        // Refresh stats to show updated counts
        setTimeout(() => fetchStats(), 1000);
      } else {
        showToast(response.data?.message || 'Failed to send media message', 'error');
      }
    } catch (error) {
      console.error('Error sending WhatsApp media message:', error);
      let errorMsg = 'Failed to send media message';
      
      if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      showToast(errorMsg, 'error');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };
  
  // Handle sending media response to a message
  const handleSendMediaResponse = async () => {
    if (!selectedMessage) {
      showToast('No message selected to respond to', 'error');
      return;
    }
    
    if (!responseMediaFile) {
      showToast('Please select a media file to send', 'error');
      return;
    }
    
    try {
      setLoading(true);
      
      // Create form data for file upload
      const formData = new FormData();
      formData.append('mediaFile', responseMediaFile);
      formData.append('mediaType', responseMediaType);
      formData.append('caption', responseMessage || '');
      
      // Add WhatsApp API configuration
      // First ensure we have the configuration
      await ensureWhatsappConfig();
      
      // Include API credentials in the request
      if (whatsappConfig.apiToken) {
        formData.append('apiToken', whatsappConfig.apiToken);
      }
      
      if (whatsappConfig.phoneNumberId) {
        formData.append('phoneNumberId', whatsappConfig.phoneNumberId);
      }
      
      // Send request to API
      const response = await api.post(`/schools/whatsapp/messages/${selectedMessage._id}/respond-media`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      if (response.data && response.data.success) {
        showToast('Media response sent successfully', 'success');
        
        // Update the message in the list
        setMessages(prevMessages => 
          prevMessages.map(msg => 
            msg._id === selectedMessage._id 
              ? { ...msg, responded: true, response: response.data.data.response }
              : msg
          )
        );
        
        // Close modal and reset state
        setShowRespondModal(false);
        setResponseMessage('');
        setSelectedMessage(null);
        setResponseMediaFile(null);
        setResponseMediaType('');
        setShowMediaUploadResponse(false);
        
        // Refresh stats to show updated sent count
        fetchStats();
      }
    } catch (error) {
      console.error('Error sending media response:', error);
      let errorMsg = 'Failed to send media response';
      
      if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      showToast(errorMsg, 'error');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };
  
  // Render upgrade message for schools without access
  if (!hasAccess) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center">
              <FaWhatsapp className="mr-2 text-green-500" /> WhatsApp Integration
            </h1>
            <p className="text-gray-600 mt-1">
              Communicate with parents and staff via WhatsApp
            </p>
          </div>
        </div>
        
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-6 text-center">
          <FaWhatsapp className="text-green-500 text-5xl mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-blue-800 mb-3">
            Upgrade to Elevate Plan for WhatsApp Integration
          </h2>
          <p className="text-blue-700 mb-6 max-w-lg mx-auto">
            The WhatsApp integration allows schools to send messages, announcements, and updates
            directly to parents and staff using WhatsApp. Upgrade to our Elevate plan to unlock
            this powerful communication feature.
          </p>
          <a href="/school/subscription" className="btn-primary">
            Upgrade Now
          </a>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaWhatsapp className="mr-2 text-green-500" /> WhatsApp Integration
          </h1>
          <p className="text-gray-600 mt-1">
            Communicate with parents and staff via WhatsApp
          </p>
        </div>
        
        {connected && (
          <div className="mt-4 md:mt-0">
            <button
              onClick={handleDisconnect}
              className="btn-secondary flex items-center"
              disabled={loading}
            >
              Disconnect WhatsApp
            </button>
          </div>
        )}
      </div>
      
      {/* WhatsApp Connection Status */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-6 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : !connected ? (
        <div className="bg-white rounded-lg shadow-soft p-8">
          <div className="text-center mb-8">
            <FaWhatsapp className="text-green-500 text-5xl mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-3">Connect to WhatsApp Business API</h2>
            <p className="text-gray-600 mb-6 max-w-lg mx-auto">
              To start sending WhatsApp messages, you need to connect to the WhatsApp Business API.
              Enter your API credentials below.
            </p>
          </div>
          
          <div className="max-w-xl mx-auto">
            <div className="space-y-6">
              <div>
                <label htmlFor="apiToken" className="block text-sm font-medium text-gray-700 mb-1">
                  WhatsApp API Token <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaKey className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="apiToken"
                    name="apiToken"
                    value={whatsappConfig.apiToken}
                    onChange={handleConfigChange}
                    className="form-input pl-10 w-full"
                    placeholder="Enter your WhatsApp API Token"
                    required
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  API Token from the WhatsApp Business Platform
                </p>
              </div>
              
              <div>
                <label htmlFor="phoneNumberId" className="block text-sm font-medium text-gray-700 mb-1">
                  WhatsApp Phone Number ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="phoneNumberId"
                  name="phoneNumberId"
                  value={whatsappConfig.phoneNumberId}
                  onChange={handleConfigChange}
                  className="form-input w-full"
                  placeholder="Enter your WhatsApp Phone Number ID"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  The ID of the phone number connected to WhatsApp Business API
                </p>
              </div>
              
              <div>
                <label htmlFor="verifyToken" className="block text-sm font-medium text-gray-700 mb-1">
                  Webhook Verify Token <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="verifyToken"
                  name="verifyToken"
                  value={whatsappConfig.verifyToken}
                  onChange={handleConfigChange}
                  className="form-input w-full"
                  placeholder="Enter your Webhook Verify Token"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  Token used to verify webhook endpoints (use simple tokens like "meira123")
                </p>
              </div>
              
              <div>
                <label htmlFor="appId" className="block text-sm font-medium text-gray-700 mb-1">
                  Facebook App ID
                </label>
                <input
                  type="text"
                  id="appId"
                  name="appId"
                  value={whatsappConfig.appId}
                  onChange={handleConfigChange}
                  className="form-input w-full"
                  placeholder="Enter your Facebook App ID (for manual webhook setup)"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Optional: App ID for manual webhook configuration
                </p>
              </div>
              
              
              <div className="pt-4">
                <button
                  onClick={handleConnect}
                  className="btn-primary flex items-center mx-auto"
                  disabled={loading}
                >
                  <FaWhatsapp className="mr-2" /> Connect WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          {/* Add webhook troubleshooting section */}
          <div className="mb-6 bg-white rounded-lg shadow-soft p-4">
            <div className="flex justify-between items-center">
              <h2 className="font-semibold text-lg flex items-center">
                <FaCog className="mr-2 text-primary" /> WhatsApp Connection
              </h2>
              <div>
                <button
                  onClick={fetchWebhookStatus}
                  className="text-sm text-primary hover:underline flex items-center"
                >
                  <FaQuestionCircle className="mr-1" /> Check Webhook Status
                </button>
              </div>
            </div>
            
            </div>
          
          {/* Tabs Navigation */}
          <div className="mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex">
                <button
                  onClick={() => setActiveTab(TABS.MAIN)}
                  className={`py-4 px-6 font-medium text-sm ${
                    activeTab === TABS.MAIN
                      ? 'border-b-2 border-primary text-primary'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Messaging
                </button>
                <button
                  onClick={() => setActiveTab(TABS.INBOX)}
                  className={`py-4 px-6 font-medium text-sm ${
                    activeTab === TABS.INBOX
                      ? 'border-b-2 border-primary text-primary'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Inbox
                </button>
                <button
                  onClick={() => setActiveTab(TABS.POLLS)}
                  className={`py-4 px-6 font-medium text-sm ${
                    activeTab === TABS.POLLS
                      ? 'border-b-2 border-primary text-primary'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Polls
                </button>
              </nav>
            </div>
          </div>
          
          {/* Main Messaging Tab */}
          {activeTab === TABS.MAIN && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Contacts & Groups */}
              <div className="lg:col-span-1 bg-white rounded-lg shadow-soft overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="font-semibold text-lg flex items-center">
                    <FaUsers className="mr-2 text-primary" /> Contacts & Groups
                  </h2>
                </div>
                
                <div className="p-4 border-b border-gray-200">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search contacts or groups..."
                      className="form-input pl-8 w-full"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaUsers className="text-gray-400" />
                    </div>
                  </div>
                  
                  <div className="flex justify-between mt-3">
                    <div>
                      <input
                        type="file"
                        id="contactsFile"
                        className="hidden"
                        accept=".csv,.xlsx"
                        onChange={handleFileSelect}
                      />
                      <label
                        htmlFor="contactsFile"
                        className="cursor-pointer text-primary hover:text-primary-dark text-sm flex items-center"
                      >
                        <FaFileDownload className="mr-1" /> Import contacts
                      </label>
                    </div>
                    <button
                      onClick={() => setShowAddContactModal(true)}
                      className="text-primary hover:text-primary-dark text-sm flex items-center"
                    >
                      <FaUserPlus className="mr-1" /> Add contact
                    </button>
                  </div>
                  
                  <div 
                    className={`mt-3 border-2 border-dashed rounded-md p-3 text-center ${
                      isDragging ? 'border-primary bg-primary-50' : 'border-gray-300'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <p className="text-xs text-gray-500">
                      {isDragging 
                        ? 'Drop your file here' 
                        : 'Drag and drop CSV or Excel file here'}
                    </p>
                  </div>
                </div>
                
                <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                  {/* Group Management */}
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-sm font-medium text-gray-500">Contact Management</h3>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setShowCreateGroupModal(true)}
                          className="text-xs text-primary hover:text-primary-dark flex items-center"
                        >
                          <FaUserPlus className="mr-1" /> New Group
                        </button>
                        {selectedContactsForGroup.length > 0 && (
                          <button
                            onClick={() => setShowAddToGroupModal(true)}
                            className="text-xs bg-primary text-white px-2 py-1 rounded hover:bg-primary-dark flex items-center"
                          >
                            <FaUsers className="mr-1" /> Add to Group ({selectedContactsForGroup.length})
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Individual Contacts */}
                  {filteredContacts.length > 0 && (
                    <div className="p-4 border-b border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-sm font-medium text-gray-500">Contacts</h3>
                        <div className="text-xs text-gray-400">
                          {selectedContactsForGroup.length > 0 ? (
                            <span>{selectedContactsForGroup.length} selected for group management</span>
                          ) : (
                            <span>Click contacts to select for group management</span>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        {filteredContacts.map(contact => (
                          <div
                            key={contact.id}
                            className={`p-2 rounded-md cursor-pointer flex items-center ${
                              selectedRecipients.some(r => r.id === contact.id)
                                ? 'bg-primary-50 border border-primary-200'
                                : 'hover:bg-gray-50'
                            } ${!contact.number ? 'opacity-50' : ''}`}
                          >
                            <div 
                              className="flex items-center mr-3"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleContactForGroup(contact.id || contact._id);
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedContactsForGroup.includes(contact.id || contact._id)}
                                onChange={() => {}}
                                className="form-checkbox h-4 w-4 text-primary mr-2"
                              />
                            </div>
                            <div 
                              className="flex items-center flex-1"
                              onClick={() => handleSelectRecipient(contact)}
                            >
                              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                                {contact.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1">
                                <div className="font-medium">
                                  {contact.name}
                                  {!contact.number && (
                                    <span className="text-red-500 ml-2 text-xs">(No valid number)</span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500">{contact.number || 'No number available'}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Groups */}
                  {filteredGroups.length > 0 && (
                    <div className="p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Groups</h3>
                      <div className="space-y-2">
                        {filteredGroups.map(group => (
                          <div
                            key={group.id}
                            className={`p-2 rounded-md cursor-pointer flex items-center ${
                              selectedRecipients.some(r => r.id === group.id)
                                ? 'bg-primary-50 border border-primary-200'
                                : 'hover:bg-gray-50'
                            }`}
                            onClick={() => handleSelectRecipient(group)}
                          >
                            <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center mr-3">
                              <FaUsers className="text-gray-500" />
                            </div>
                            <div className="flex-1">
                              <div className="font-medium">{group.name}</div>
                              <div className="text-xs text-gray-500">{group.memberCount} members</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {filteredContacts.length === 0 && filteredGroups.length === 0 && (
                    <div className="p-6 text-center text-gray-500">
                      No contacts or groups found
                    </div>
                  )}
                </div>
              </div>
              
              {/* Message Composer */}
              <div className="lg:col-span-2 bg-white rounded-lg shadow-soft overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="font-semibold text-lg flex items-center">
                    <FaPaperPlane className="mr-2 text-primary" /> Compose Message
                  </h2>
                </div>
                
                <div className="p-6">
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Recipients</label>
                    <div className="p-2 border rounded-md bg-gray-50 min-h-[40px]">
                      {selectedRecipients.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {selectedRecipients.map(recipient => (
                            <div key={recipient.id} className="bg-primary text-white text-sm rounded-full px-3 py-1 flex items-center">
                              {recipient.name}
                              <button
                                className="ml-2 text-white hover:text-red-200"
                                onClick={() => handleSelectRecipient(recipient)}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-gray-400 text-sm">
                          Select recipients from the contact list
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <div className="flex justify-between">
                      <label className="block text-sm font-medium mb-1">Message</label>
                      <div>
                        <select
                          className="form-select text-sm"
                          value={selectedTemplate ? selectedTemplate.id : ''}
                          onChange={(e) => {
                            const template = templates.find(t => t.id === e.target.value);
                            if (template) handleSelectTemplate(template);
                          }}
                        >
                          <option value="">-- Select Template --</option>
                          {templates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => setShowNewTemplateModal(true)}
                          className="ml-2 text-primary text-sm hover:underline"
                        >
                          + New Template
                        </button>
                      </div>
                    </div>
                    <textarea
                      className="form-textarea w-full"
                      rows="6"
                      placeholder="Type your message here..."
                      value={message}
                      onChange={handleMessageChange}
                    ></textarea>
                    <div className="text-right text-xs text-gray-500 mt-1">
                      {message.length} characters
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <div>
                      <button className="text-gray-500 hover:text-primary text-sm flex items-center">
                        <FaQuestionCircle className="mr-1" /> Message guidelines
                      </button>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowMediaUpload(!showMediaUpload)}
                        className="btn-secondary flex items-center"
                        type="button"
                      >
                        {showMediaUpload ? 'Switch to Text Message' : 'Send Media/Document'}
                      </button>
                      {showMediaUpload ? (
                        <button
                          onClick={handleSendMediaMessage}
                          className="btn-primary flex items-center"
                          disabled={loading || !mediaFile || selectedRecipients.length === 0}
                        >
                          {loading ? (
                            <>
                              <LoadingSpinner size="sm" className="mr-2" /> Sending...
                            </>
                          ) : (
                            <>
                              <FaPaperPlane className="mr-2" /> Send Media
                            </>
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={handleSendMessage}
                          className="btn-primary flex items-center"
                          disabled={loading || !message.trim() || selectedRecipients.length === 0}
                        >
                          {loading ? (
                            <>
                              <LoadingSpinner size="sm" className="mr-2" /> Sending...
                            </>
                          ) : (
                            <>
                              <FaPaperPlane className="mr-2" /> Send Message
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Media file upload section */}
                  {showMediaUpload && (
                    <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
                      <h3 className="text-sm font-medium mb-2">Upload Media</h3>
                      <div className="mb-3">
                        <input
                          type="file"
                          id="mediaFile"
                          className="hidden"
                          onChange={handleMediaFileSelect}
                        />
                        <label htmlFor="mediaFile" className="btn-secondary text-sm cursor-pointer">
                          Choose File
                        </label>
                        {mediaFile && (
                          <span className="ml-2 text-sm">
                            {mediaFile.name} ({Math.round(mediaFile.size / 1024)} KB)
                          </span>
                        )}
                      </div>
                      
                      {mediaFile && (
                        <>
                          <div className="mb-3">
                            <label className="block text-sm font-medium mb-1">Media Type</label>
                            <select
                              className="form-select w-full"
                              value={mediaType}
                              onChange={(e) => setMediaType(e.target.value)}
                            >
                              <option value="image">Image</option>
                              <option value="document">Document</option>
                              <option value="audio">Audio</option>
                              <option value="video">Video</option>
                            </select>
                          </div>
                          
                          <div className="mb-3">
                            <label className="block text-sm font-medium mb-1">Caption (Optional)</label>
                            <textarea
                              className="form-textarea w-full"
                              rows="2"
                              placeholder="Enter caption for the media..."
                              value={mediaCaption}
                              onChange={(e) => setMediaCaption(e.target.value)}
                            ></textarea>
                          </div>
                          
                          {uploadProgress > 0 && (
                            <div className="mb-3">
                              <label className="block text-sm font-medium mb-1">Upload Progress</label>
                              <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div
                                  className="bg-primary h-2.5 rounded-full"
                                  style={{ width: `${uploadProgress}%` }}
                                ></div>
                              </div>
                              <div className="text-right text-xs mt-1">{uploadProgress}%</div>
                            </div>
                          )}
                          
                          <div className="text-xs text-gray-500 mt-2">
                            <p>Note: Media types must match the actual file content.</p>
                            <p>Maximum file size: 16MB</p>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* WhatsApp Messaging Statistics */}
              <div className="lg:col-span-3 bg-white rounded-lg shadow-soft overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h2 className="font-semibold text-lg flex items-center">
                    <FaCog className="mr-2 text-primary" /> Messaging Statistics
                  </h2>
                  <div className="flex space-x-3">
                    {statsLoading && <LoadingSpinner size="sm" />}
                    
                    {/* Add inbox button */}
                    {connected && (
                      <button
                        onClick={() => setActiveTab(TABS.INBOX)}
                        className="btn-secondary flex items-center text-sm"
                      >
                        {activeTab === TABS.INBOX ? (
                          <>
                            <FaTimes className="mr-1" /> Close Inbox
                          </>
                        ) : (
                          <>
                            <FaInbox className="mr-1" /> View Messages Inbox
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                    <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                      <div className="text-green-500 text-2xl font-bold">{stats.sent}</div>
                      <div className="text-green-600">Messages Sent</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                      <div className="text-blue-500 text-2xl font-bold">{stats.delivered}</div>
                      <div className="text-blue-600 flex items-center">
                        Delivered
                        <span className="ml-1 cursor-help group relative">
                          <FaQuestionCircle className="text-blue-400 text-xs" />
                          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 hidden group-hover:block bg-gray-800 text-white text-xs rounded p-2 w-48">
                            Number of messages successfully delivered to recipient devices
                          </span>
                        </span>
                      </div>
                    </div>
                    <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100">
                      <div className="text-indigo-500 text-2xl font-bold">{stats.read}</div>
                      <div className="text-indigo-600 flex items-center">
                        Read
                        <span className="ml-1 cursor-help group relative">
                          <FaQuestionCircle className="text-indigo-400 text-xs" />
                          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 hidden group-hover:block bg-gray-800 text-white text-xs rounded p-2 w-48">
                            Number of messages that have been read by recipients
                          </span>
                        </span>
                      </div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                      <div className="text-red-500 text-2xl font-bold">{stats.failed}</div>
                      <div className="text-red-600">Failed</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                      <div className="text-purple-500 text-2xl font-bold">{stats.received}</div>
                      <div className="text-purple-600">Received</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Inbox Tab */}
          {activeTab === TABS.INBOX && (
            <div className="bg-white rounded-lg shadow-soft overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="font-semibold text-lg flex items-center">
                  <FaInbox className="mr-2 text-primary" /> Messages Inbox
                </h2>
              </div>
              
              <div className="p-4 border-b border-gray-200">
                <div className="flex flex-col md:flex-row items-center justify-between space-y-2 md:space-y-0">
                  <div className="relative md:w-72">
                    <input
                      type="text"
                      placeholder="Search messages..."
                      className="form-input pl-8 w-full"
                      value={messagesSearch}
                      onChange={handleMessagesSearch}
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaSearch className="text-gray-400" />
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        className="form-checkbox mr-2"
                        checked={showOnlyUnread}
                        onChange={() => setShowOnlyUnread(!showOnlyUnread)}
                      />
                      Only show unread messages
                    </label>
                    
                    <button
                      onClick={fetchMessages}
                      className="btn-secondary text-sm"
                      disabled={messagesLoading}
                    >
                      {messagesLoading ? <LoadingSpinner size="sm" /> : 'Refresh'}
                    </button>
                  </div>
                </div>
              </div>
              
              {messagesLoading && messages.length === 0 ? (
                <div className="p-6 text-center">
                  <LoadingSpinner size="lg" />
                  <p className="mt-2 text-gray-500">Loading messages...</p>
                </div>
              ) : messages.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <FaInbox className="mx-auto text-3xl mb-2 text-gray-300" />
                  <p>No messages found</p>
                  <p className="text-sm">Messages received from your contacts will appear here</p>
                </div>
              ) : (
                <div>
                  <div className="overflow-y-auto" style={{ maxHeight: '500px' }}>
                    {messages.map(msg => (
                      <div 
                        key={msg._id} 
                        className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${!msg.read ? 'bg-blue-50' : ''}`}
                        onClick={() => handleSelectMessage(msg)}
                      >
                        <div className="flex justify-between">
                          <div className="font-medium flex items-center">
                            {!msg.read && <FaBell className="text-primary mr-1" title="Unread message" />}
                            {msg.senderName || formatPhoneNumber(msg.from)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatDateTime(msg.createdAt)}
                          </div>
                        </div>
                        
                        {/* Display different message types */}
                        <div className="mt-1">
                          {msg.messageType === 'text' ? (
                            <div className="text-gray-700">{msg.message}</div>
                          ) : msg.messageType === 'image' && msg.mediaUrl ? (
                            <div className="flex items-start">
                              <div className="w-16 h-16 rounded-md overflow-hidden mr-3">
                                <img src={msg.mediaUrl} alt="Received" className="w-full h-full object-cover" />
                              </div>
                              <div className="flex-1">
                                <div className="text-gray-700">{msg.message || 'Image received'}</div>
                                <a 
                                  href={msg.mediaUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="text-primary text-xs hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  View Image
                                </a>
                              </div>
                            </div>
                          ) : msg.messageType === 'document' && msg.mediaUrl ? (
                            <div className="flex items-start">
                              <div className="flex-shrink-0 mr-3">
                                <span className="bg-gray-200 rounded p-2 inline-block">
                                  <FaFileDownload className="text-primary text-lg" />
                                </span>
                              </div>
                              <div className="flex-1">
                                <div className="text-gray-700">{msg.message || 'Document received'}</div>
                                <a 
                                  href={msg.mediaUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="text-primary text-xs hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Download Document
                                </a>
                              </div>
                            </div>
                          ) : (msg.messageType === 'video' || msg.messageType === 'audio') && msg.mediaUrl ? (
                            <div className="flex items-start">
                              <div className="flex-shrink-0 mr-3">
                                <span className="bg-gray-200 rounded p-2 inline-block">
                                  {msg.messageType === 'video' ? (
                                    <FaVideo className="text-primary text-lg" />
                                  ) : (
                                    <FaMusic className="text-primary text-lg" />
                                  )}
                                </span>
                              </div>
                              <div className="flex-1">
                                <div className="text-gray-700">
                                  {msg.message || `${msg.messageType.charAt(0).toUpperCase() + msg.messageType.slice(1)} received`}
                                </div>
                                <a 
                                  href={msg.mediaUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="text-primary text-xs hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {msg.messageType === 'video' ? 'Watch Video' : 'Listen to Audio'}
                                </a>
                              </div>
                            </div>
                          ) : (
                            <div className="text-gray-700">
                              {msg.message || `${msg.messageType.charAt(0).toUpperCase() + msg.messageType.slice(1)} message`}
                            </div>
                          )}
                        </div>
                        
                        {msg.responded && (
                          <div className="mt-2 text-sm text-gray-500 flex items-center">
                            <FaCheckCircle className="text-green-500 mr-1" /> 
                            {msg.response?.mediaUrl ? (
                              <span>Responded with media</span>
                            ) : (
                              <span>Responded: {msg.response?.message}</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Pagination */}
                  {messagesTotalPages > 1 && (
                    <div className="p-4 flex justify-center">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setMessagesPage(prev => Math.max(1, prev - 1))}
                          disabled={messagesPage === 1}
                          className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50"
                        >
                          Prev
                        </button>
                        <span className="px-3 py-1 bg-gray-100 rounded">
                          {messagesPage} of {messagesTotalPages}
                        </span>
                        <button
                          onClick={() => setMessagesPage(prev => Math.min(messagesTotalPages, prev + 1))}
                          disabled={messagesPage === messagesTotalPages}
                          className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Polls Tab */}
          {activeTab === TABS.POLLS && (
            <WhatsAppPolls />
          )}
        </div>
      )}
      
      {/* New Template Modal */}
      <Modal
        isOpen={showNewTemplateModal}
        onClose={() => setShowNewTemplateModal(false)}
        title="Create Message Template"
      >
        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Template Name</label>
            <input
              type="text"
              name="name"
              className="form-input w-full"
              value={newTemplate.name}
              onChange={handleTemplateChange}
              placeholder="E.g., Fee Reminder"
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1">Template Content</label>
            <textarea
              name="content"
              className="form-textarea w-full"
              rows="5"
              value={newTemplate.content}
              onChange={handleTemplateChange}
              placeholder="Type your template message here..."
            ></textarea>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowNewTemplateModal(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateTemplate}
              className="btn-primary"
            >
              Create Template
            </button>
          </div>
        </div>
      </Modal>
      
      {/* Add Contact Modal */}
      <Modal
        isOpen={showAddContactModal}
        onClose={() => setShowAddContactModal(false)}
        title="Add WhatsApp Contact"
      >
        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Contact Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              name="name"
              className="form-input w-full"
              value={newContact.name}
              onChange={handleContactChange}
              placeholder="Enter contact name"
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Phone Number <span className="text-red-500">*</span></label>
            <input
              type="tel"
              name="number"
              className="form-input w-full"
              value={newContact.number}
              onChange={handleContactChange}
              placeholder="Include country code (e.g., +2547XXXXXXXX)"
            />
            <p className="text-xs text-gray-500 mt-1">
              Include country code, e.g., +254 for Kenya
            </p>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1">Role</label>
            <select
              name="role"
              className="form-select w-full"
              value={newContact.role}
              onChange={handleContactChange}
            >
              <option value="parent">Parent</option>
              <option value="teacher">Teacher</option>
              <option value="staff">Staff</option>
              <option value="student">Student</option>
              <option value="other">Other</option>
            </select>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowAddContactModal(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleAddContact}
              className="btn-primary"
            >
              Add Contact
            </button>
          </div>
        </div>
      </Modal>
      
      {/* Import Contacts Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => !isUploading && setShowImportModal(false)}
        title="Import WhatsApp Contacts"
      >
        <div className="p-6">
          <div className="mb-6">
            <h3 className="font-medium mb-2">Selected File:</h3>
            <div className="bg-gray-50 p-3 rounded border">
              {importFile && (
                <div className="flex items-center">
                  <div className="mr-2">
                    <FaFileDownload className="text-primary" />
                  </div>
                  <div className="flex-1 truncate">
                    {importFile.name}
                    <span className="text-gray-500 text-sm ml-2">
                      ({Math.round(importFile.size / 1024)} KB)
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="font-medium mb-2">File Format Requirements:</h3>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              <li>CSV or Excel (.xlsx) file format</li>
              <li>File must contain <strong>name</strong> and <strong>number</strong> columns</li>
              <li>Phone numbers should include country code (e.g., +254712345678)</li>
              <li>Optional columns: <em>role</em> (parent, teacher, staff, student, other)</li>
              <li>Maximum 1000 contacts per import</li>
            </ul>
            <button
              onClick={handleDownloadSample}
              className="text-primary text-sm flex items-center mt-2 hover:underline"
            >
              <FaFileDownload className="mr-1" /> Download sample template
            </button>
          </div>
          
          <div className="mb-6">
            <h3 className="font-medium mb-2">Add to Group (Optional):</h3>
            <select
              value={selectedGroupForImport}
              onChange={(e) => setSelectedGroupForImport(e.target.value)}
              className="form-select w-full"
              disabled={isUploading}
            >
              <option value="">Select a group (optional)</option>
              {groups.map(group => (
                <option key={group._id} value={group._id}>
                  {group.name} ({group.memberCount || 0} members)
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Imported contacts will be automatically added to the selected group
            </p>
          </div>
          
          {isUploading && (
            <div className="mb-6">
              <h3 className="font-medium mb-2">Upload Progress:</h3>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-primary h-2.5 rounded-full" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <div className="text-right text-xs mt-1">{uploadProgress}%</div>
            </div>
          )}
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowImportModal(false)}
              className="btn-secondary"
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              onClick={handleImportContacts}
              className="btn-primary flex items-center"
              disabled={isUploading || !importFile}
            >
              {isUploading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" /> Importing...
                </>
              ) : (
                <>
                  <FaFileDownload className="mr-2" /> Import Contacts
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
      
      {/* Respond to Message Modal */}
      <Modal
        isOpen={showRespondModal}
        onClose={() => {
          setShowRespondModal(false);
          setResponseMessage('');
        }}
        title="Respond to Message"
      >
        <div className="p-6">
          {selectedMessage && (
            <div className="mb-6">
              <div className="mb-2">
                <span className="text-sm font-medium text-gray-500">From:</span>
                <span className="ml-2">{selectedMessage.senderName || formatPhoneNumber(selectedMessage.from)}</span>
              </div>
              <div className="mb-2">
                <span className="text-sm font-medium text-gray-500">Received:</span>
                <span className="ml-2">{formatDateTime(selectedMessage.createdAt)}</span>
              </div>
              <div className="p-3 bg-gray-50 rounded-md">
                {selectedMessage.messageType === 'text' ? (
                  <p className="whitespace-pre-wrap">{selectedMessage.message}</p>
                ) : selectedMessage.messageType === 'image' && selectedMessage.mediaUrl ? (
                  <div>
                    <img 
                      src={selectedMessage.mediaUrl} 
                      alt="Received" 
                      className="max-w-full rounded-md mb-2" 
                      style={{ maxHeight: '200px' }}
                    />
                    <p className="whitespace-pre-wrap">{selectedMessage.message}</p>
                    <div className="mt-2">
                      <a 
                        href={selectedMessage.mediaUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-primary text-sm hover:underline"
                      >
                        View Full Image
                      </a>
                    </div>
                  </div>
                ) : selectedMessage.messageType === 'document' && selectedMessage.mediaUrl ? (
                  <div>
                    <div className="flex items-center mb-2">
                      <FaFileDownload className="text-primary text-lg mr-2" />
                      <span>Document received</span>
                    </div>
                    <p className="whitespace-pre-wrap">{selectedMessage.message}</p>
                    <div className="mt-2">
                      <a 
                        href={selectedMessage.mediaUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-primary text-sm hover:underline"
                      >
                        Download Document
                      </a>
                    </div>
                  </div>
                ) : (selectedMessage.messageType === 'video' || selectedMessage.messageType === 'audio') && selectedMessage.mediaUrl ? (
                  <div>
                    <div className="flex items-center mb-2">
                      {selectedMessage.messageType === 'video' ? (
                        <FaVideo className="text-primary text-lg mr-2" />
                      ) : (
                        <FaMusic className="text-primary text-lg mr-2" />
                      )}
                      <span>{selectedMessage.messageType === 'video' ? 'Video' : 'Audio'} received</span>
                    </div>
                    <p className="whitespace-pre-wrap">{selectedMessage.message}</p>
                    <div className="mt-2">
                      {selectedMessage.messageType === 'video' ? (
                        <video controls className="max-w-full rounded-md" style={{ maxHeight: '200px' }}>
                          <source src={selectedMessage.mediaUrl} />
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        <audio controls className="w-full">
                          <source src={selectedMessage.mediaUrl} />
                          Your browser does not support the audio tag.
                        </audio>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap">{selectedMessage.message}</p>
                )}
              </div>
            </div>
          )}
          
          {/* WhatsApp configuration status */}
          {(!whatsappConfig.apiToken || !whatsappConfig.phoneNumberId) && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-md">
              <p className="text-yellow-800 text-sm font-medium">
                <FaExclamationTriangle className="inline mr-1" /> WhatsApp configuration is incomplete
              </p>
              <p className="text-yellow-700 text-xs mt-1">
                Make sure you are connected to WhatsApp and have valid API credentials.
              </p>
            </div>
          )}
          
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1">Your Response</label>
            <div className="flex items-center mb-2">
              <button
                onClick={() => setShowMediaUploadResponse(!showMediaUploadResponse)}
                className="text-primary hover:text-primary-dark text-sm flex items-center"
              >
                {showMediaUploadResponse ? 'Switch to Text Response' : 'Send Media/Document Response'}
              </button>
            </div>
            
            {showMediaUploadResponse ? (
              <div className="p-3 border rounded-md bg-gray-50">
                <div className="mb-3">
                  <input
                    type="file"
                    id="responseMediaFile"
                    className="hidden"
                    onChange={handleResponseMediaFileSelect}
                  />
                  <label htmlFor="responseMediaFile" className="btn-secondary text-sm cursor-pointer">
                    Choose Media File
                  </label>
                  {responseMediaFile && (
                    <span className="ml-2 text-sm">
                      {responseMediaFile.name} ({Math.round(responseMediaFile.size / 1024)} KB)
                    </span>
                  )}
                </div>
                
                {responseMediaFile && (
                  <>
                    <div className="mb-3">
                      <label className="block text-sm font-medium mb-1">Media Type</label>
                      <select
                        className="form-select w-full"
                        value={responseMediaType}
                        onChange={(e) => setResponseMediaType(e.target.value)}
                      >
                        <option value="image">Image</option>
                        <option value="document">Document</option>
                        <option value="audio">Audio</option>
                        <option value="video">Video</option>
                      </select>
                    </div>
                    
                    <div className="mb-2">
                      <label className="block text-sm font-medium mb-1">Caption (Optional)</label>
                      <textarea
                        className="form-textarea w-full"
                        rows="2"
                        value={responseMessage}
                        onChange={(e) => setResponseMessage(e.target.value)}
                        placeholder="Type your caption here..."
                      ></textarea>
                    </div>
                    
                    {uploadProgress > 0 && (
                      <div className="mb-2">
                        <label className="block text-sm font-medium mb-1">Upload Progress</label>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            className="bg-primary h-2.5 rounded-full"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <div className="text-right text-xs mt-1">{uploadProgress}%</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <textarea
                className="form-textarea w-full"
                rows="4"
                value={responseMessage}
                onChange={(e) => setResponseMessage(e.target.value)}
                placeholder="Type your response here..."
              ></textarea>
            )}
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {
                setShowRespondModal(false);
                setResponseMessage('');
                setResponseMediaFile(null);
                setResponseMediaType('');
                setShowMediaUploadResponse(false);
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            {showMediaUploadResponse ? (
              <button
                onClick={handleSendMediaResponse}
                className="btn-primary flex items-center"
                disabled={loading || !responseMediaFile || !responseMediaType}
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" /> Sending...
                  </>
                ) : (
                  <>
                    <FaReply className="mr-2" /> Send Media Response
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleSendResponse}
                className="btn-primary flex items-center"
                disabled={loading || !responseMessage.trim() || !whatsappConfig.apiToken || !whatsappConfig.phoneNumberId}
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" /> Sending...
                  </>
                ) : (
                  <>
                    <FaReply className="mr-2" /> Send Response
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </Modal>
      
      {/* Create Group Modal */}
      <Modal
        isOpen={showCreateGroupModal}
        onClose={() => setShowCreateGroupModal(false)}
        title="Create New Contact Group"
      >
        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Group Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              className="form-input w-full"
              value={newGroup.name}
              onChange={(e) => setNewGroup(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter group name"
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              className="form-textarea w-full"
              rows="3"
              value={newGroup.description}
              onChange={(e) => setNewGroup(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter group description (optional)"
            ></textarea>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowCreateGroupModal(false)}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleCreateGroup}
              className="btn-primary flex items-center"
              disabled={loading || !newGroup.name.trim()}
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" /> Creating...
                </>
              ) : (
                <>
                  <FaUsers className="mr-2" /> Create Group
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
      
      {/* Add Contacts to Group Modal */}
      <Modal
        isOpen={showAddToGroupModal}
        onClose={() => {
          setShowAddToGroupModal(false);
          setSelectedContactsForGroup([]);
          setSelectedGroupForAdd('');
        }}
        title="Add Contacts to Group"
      >
        <div className="p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-3">
              You have selected {selectedContactsForGroup.length} contacts to add to a group.
            </p>
            
            <label className="block text-sm font-medium mb-1">Select Group <span className="text-red-500">*</span></label>
            <select
              value={selectedGroupForAdd}
              onChange={(e) => setSelectedGroupForAdd(e.target.value)}
              className="form-select w-full"
              disabled={loading}
            >
              <option value="">Choose a group</option>
              {groups.map(group => (
                <option key={group._id} value={group._id}>
                  {group.name} ({group.memberCount || 0} members)
                </option>
              ))}
            </select>
          </div>
          
          <div className="mb-4">
            <h4 className="text-sm font-medium mb-2">Selected Contacts:</h4>
            <div className="max-h-32 overflow-y-auto border rounded p-2">
              {selectedContactsForGroup.map(contactId => {
                const contact = contacts.find(c => (c.id || c._id) === contactId);
                return contact ? (
                  <div key={contactId} className="flex items-center py-1">
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-2 text-xs">
                      {contact.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm">{contact.name}</span>
                  </div>
                ) : null;
              })}
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {
                setShowAddToGroupModal(false);
                setSelectedContactsForGroup([]);
                setSelectedGroupForAdd('');
              }}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleAddContactsToGroup}
              className="btn-primary flex items-center"
              disabled={loading || !selectedGroupForAdd || selectedContactsForGroup.length === 0}
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" /> Adding...
                </>
              ) : (
                <>
                  <FaUsers className="mr-2" /> Add to Group
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SchoolWhatsappIntegration; 